import { useEffect, useState, useRef, useMemo } from "react";
import { UserPlus, Save, X } from "lucide-react";
import api from "../services/api";
import Swal from "sweetalert2";

export default function ClientForm({
  fetchClients,
  editingClient,
  setEditingClient,
  onCancel,
}) {
  const nombreRef = useRef(null);
  const isLoadingClient = useRef(false);

  const [nombre, setNombre] = useState("");
  const [nrc, setNrc] = useState("");
  const [direccion, setDireccion] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");
  const [tipoCliente, setTipoCliente] = useState("No Contribuyente");
  const [departamento, setDepartamento] = useState("");
  const [municipio, setMunicipio] = useState("");
  const [nombreComercial, setNombreComercial] = useState("");
  const [tipoDocumento, setTipoDocumento] = useState("");
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [codActividad, setCodActividad] = useState("");
  const [descActividad, setDescActividad] = useState("");
  const [actividades, setActividades] = useState([]);
  const [loadingActividades, setLoadingActividades] = useState(false);
  const [actividadSearch, setActividadSearch] = useState('');
  const [showActividadDropdown, setShowActividadDropdown] = useState(false);
  const [filteredActividades, setFilteredActividades] = useState([]);

  // ========================================
  // ✅ FUNCIONES DE FORMATEO
  // ========================================
  
  const validarNIT = (nit) => {
    const nitLimpio = nit.replace(/-/g, '');
    const formato14 = /^\d{14}$/;
    const formato9 = /^\d{9}$/;
    return formato14.test(nitLimpio) || formato9.test(nitLimpio);
  };

  const formatearNIT = (valor) => {
    const numeros = valor.replace(/\D/g, '');
    
    if (numeros.length <= 9) {
      if (numeros.length > 8) {
        return `${numeros.slice(0, 8)}-${numeros.slice(8)}`;
      }
      return numeros;
    } else {
      if (numeros.length > 13) {
        return `${numeros.slice(0, 4)}-${numeros.slice(4, 10)}-${numeros.slice(10, 13)}-${numeros.slice(13, 14)}`;
      } else if (numeros.length > 10) {
        return `${numeros.slice(0, 4)}-${numeros.slice(4, 10)}-${numeros.slice(10)}`;
      } else if (numeros.length > 4) {
        return `${numeros.slice(0, 4)}-${numeros.slice(4)}`;
      }
      return numeros;
    }
  };

  const formatearDUI = (valor) => {
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length > 8) {
      return `${numeros.slice(0, 8)}-${numeros.slice(8, 9)}`;
    }
    return numeros;
  };

  // ========================================
  // ✅ CATÁLOGOS OFICIALES DE HACIENDA
  // ========================================

  const tiposDocumento = [
    { codigo: "36", nombre: "NIT" },
    { codigo: "13", nombre: "DUI" },
    { codigo: "03", nombre: "Pasaporte" },
    { codigo: "02", nombre: "Carnet de Residente" },
    { codigo: "37", nombre: "Otro" },
  ];

  const departamentos = [
    { codigo: "00", nombre: "Otro (Para extranjeros)" },
    { codigo: "01", nombre: "AHUACHAPAN" },
    { codigo: "02", nombre: "SANTA ANA" },
    { codigo: "03", nombre: "SONSONATE" },
    { codigo: "04", nombre: "CHALATENANGO" },
    { codigo: "05", nombre: "LA LIBERTAD" },
    { codigo: "06", nombre: "SAN SALVADOR" },
    { codigo: "07", nombre: "CUSCATLAN" },
    { codigo: "08", nombre: "LA PAZ" },
    { codigo: "09", nombre: "CABAÑAS" },
    { codigo: "10", nombre: "SAN VICENTE" },
    { codigo: "11", nombre: "USULUTAN" },
    { codigo: "12", nombre: "SAN MIGUEL" },
    { codigo: "13", nombre: "MORAZAN" },
    { codigo: "14", nombre: "LA UNION" },
  ];

  // ✅ CAT-013 OFICIAL - Municipios con códigos que se repiten entre departamentos
  const todosMunicipios = [
    { departamento: "00", codigo: "00", nombre: "Otro (Para extranjeros)" },
    
    // AHUACHAPÁN (01)
    { departamento: "01", codigo: "13", nombre: "AHUACHAPAN NORTE" },
    { departamento: "01", codigo: "14", nombre: "AHUACHAPAN CENTRO" },
    { departamento: "01", codigo: "15", nombre: "AHUACHAPAN SUR" },
    
    // SANTA ANA (02)
    { departamento: "02", codigo: "14", nombre: "SANTA ANA NORTE" },
    { departamento: "02", codigo: "15", nombre: "SANTA ANA CENTRO" },
    { departamento: "02", codigo: "16", nombre: "SANTA ANA ESTE" },
    { departamento: "02", codigo: "17", nombre: "SANTA ANA OESTE" },
    
    // SONSONATE (03)
    { departamento: "03", codigo: "17", nombre: "SONSONATE NORTE" },
    { departamento: "03", codigo: "18", nombre: "SONSONATE CENTRO" },
    { departamento: "03", codigo: "19", nombre: "SONSONATE ESTE" },
    { departamento: "03", codigo: "20", nombre: "SONSONATE OESTE" },
    
    // CHALATENANGO (04)
    { departamento: "04", codigo: "34", nombre: "CHALATENANGO NORTE" },
    { departamento: "04", codigo: "35", nombre: "CHALATENANGO CENTRO" },
    { departamento: "04", codigo: "36", nombre: "CHALATENANGO SUR" },
    
    // LA LIBERTAD (05)
    { departamento: "05", codigo: "23", nombre: "LA LIBERTAD NORTE" },
    { departamento: "05", codigo: "24", nombre: "LA LIBERTAD CENTRO" },
    { departamento: "05", codigo: "25", nombre: "LA LIBERTAD OESTE" },
    { departamento: "05", codigo: "26", nombre: "LA LIBERTAD ESTE" },
    { departamento: "05", codigo: "27", nombre: "LA LIBERTAD COSTA" },
    { departamento: "05", codigo: "28", nombre: "LA LIBERTAD SUR" },
    
    // SAN SALVADOR (06)
    { departamento: "06", codigo: "20", nombre: "SAN SALVADOR NORTE" },
    { departamento: "06", codigo: "21", nombre: "SAN SALVADOR OESTE" },
    { departamento: "06", codigo: "22", nombre: "SAN SALVADOR ESTE" },
    { departamento: "06", codigo: "23", nombre: "SAN SALVADOR CENTRO" },
    { departamento: "06", codigo: "24", nombre: "SAN SALVADOR SUR" },
    
    // CUSCATLÁN (07)
    { departamento: "07", codigo: "17", nombre: "CUSCATLAN NORTE" },
    { departamento: "07", codigo: "18", nombre: "CUSCATLAN SUR" },
    
    // LA PAZ (08)
    { departamento: "08", codigo: "23", nombre: "LA PAZ OESTE" },
    { departamento: "08", codigo: "24", nombre: "LA PAZ CENTRO" },
    { departamento: "08", codigo: "25", nombre: "LA PAZ ESTE" },
    
    // CABAÑAS (09)
    { departamento: "09", codigo: "10", nombre: "CABAÑAS OESTE" },
    { departamento: "09", codigo: "11", nombre: "CABAÑAS ESTE" },
    
    // SAN VICENTE (10)
    { departamento: "10", codigo: "14", nombre: "SAN VICENTE NORTE" },
    { departamento: "10", codigo: "15", nombre: "SAN VICENTE SUR" },
    
    // USULUTÁN (11)
    { departamento: "11", codigo: "24", nombre: "USULUTAN NORTE" },
    { departamento: "11", codigo: "25", nombre: "USULUTAN ESTE" },
    { departamento: "11", codigo: "26", nombre: "USULUTAN OESTE" },
    
    // SAN MIGUEL (12)
    { departamento: "12", codigo: "21", nombre: "SAN MIGUEL NORTE" },
    { departamento: "12", codigo: "22", nombre: "SAN MIGUEL CENTRO" },
    { departamento: "12", codigo: "23", nombre: "SAN MIGUEL OESTE" },
    
    // MORAZÁN (13)
    { departamento: "13", codigo: "27", nombre: "MORAZAN NORTE" },
    { departamento: "13", codigo: "28", nombre: "MORAZAN SUR" },
    
    // LA UNIÓN (14)
    { departamento: "14", codigo: "19", nombre: "LA UNION NORTE" },
    { departamento: "14", codigo: "20", nombre: "LA UNION SUR" },
  ];

  const municipiosFiltrados = useMemo(() => {
    if (!departamento) return [];
    return todosMunicipios.filter(m => m.departamento === departamento);
  }, [departamento]);

  useEffect(() => {
    const fetchActividades = async () => {
      try {
        setLoadingActividades(true);
        const res = await api.get("/actividades-economicas");
        const actividadesData = Array.isArray(res.data) ? res.data : [];
        setActividades(actividadesData);
        setFilteredActividades(actividadesData);
      } catch (error) {
        console.error("Error cargando actividades:", error);
        Swal.fire({
          icon: "warning",
          title: "Advertencia",
          text: "No se pudieron cargar las actividades económicas",
          confirmButtonText: "Continuar",
        });
      } finally {
        setLoadingActividades(false);
      }
    };

    fetchActividades();
  }, []);

  useEffect(() => {
    if (actividadSearch.trim() === '') {
      setFilteredActividades(actividades);
    } else {
      const searchLower = actividadSearch.toLowerCase();
      const filtered = actividades.filter(act => 
        act.descripcion.toLowerCase().includes(searchLower) ||
        act.codigo.toLowerCase().includes(searchLower)
      );
      setFilteredActividades(filtered);
    }
  }, [actividadSearch, actividades]);

  useEffect(() => {
    if (tipoCliente === "Contribuyente") {
      setTipoDocumento("36");
    } else {
      setTipoDocumento("");
    }
  }, [tipoCliente]);

  useEffect(() => {
    if (!isLoadingClient.current) {
      setMunicipio("");
    }
  }, [departamento]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActividadDropdown && !event.target.closest('.actividad-search-container')) {
        setShowActividadDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActividadDropdown]);

  useEffect(() => {
    if (editingClient) {
      isLoadingClient.current = true;

      setNombre(editingClient.nombre || "");
      setNrc(editingClient.nrc || "");
      setDireccion(editingClient.direccion || "");
      setTelefono(editingClient.telefono || "");
      setEmail(editingClient.email || "");
      setTipoCliente(editingClient.tipo_cliente || "No Contribuyente");
      setDepartamento(editingClient.departamento || "");
      setMunicipio(editingClient.municipio || "");
      setNombreComercial(editingClient.nombre_comercial || "");
      setTipoDocumento(editingClient.tipo_documento || "");
      setNumeroDocumento(editingClient.numero_documento || "");
      setCodActividad(editingClient.cod_actividad || "");
      setDescActividad(editingClient.desc_actividad || "");
      setActividadSearch(editingClient.desc_actividad || "");

      setTimeout(() => {
        isLoadingClient.current = false;
      }, 100);
    } else {
      resetForm();
    }

    setTimeout(() => nombreRef.current?.focus(), 100);
  }, [editingClient]);

  const resetForm = () => {
    setNombre("");
    setNrc("");
    setDireccion("");
    setTelefono("");
    setEmail("");
    setTipoCliente("No Contribuyente");
    setDepartamento("");
    setMunicipio("");
    setNombreComercial("");
    setTipoDocumento("");
    setNumeroDocumento("");
    setCodActividad("");
    setDescActividad("");
    setActividadSearch("");
    
    setTimeout(() => nombreRef.current?.focus(), 50);
  };

  const handleActividadSelect = (actividad) => {
    setCodActividad(actividad.codigo);
    setDescActividad(actividad.descripcion);
    setActividadSearch(actividad.descripcion);
    setShowActividadDropdown(false);
  };

  const handleActividadInputChange = (e) => {
    const value = e.target.value;
    setActividadSearch(value);
    setShowActividadDropdown(true);
    
    if (value.trim() === '') {
      setCodActividad('');
      setDescActividad('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (tipoCliente === "Contribuyente") {
      if (!tipoDocumento || !numeroDocumento) {
        Swal.fire("Error", "El tipo y número de documento son obligatorios para contribuyentes", "error");
        return;
      }
      
      if (tipoDocumento === "36") {
        if (!validarNIT(numeroDocumento)) {
          Swal.fire({
            icon: "error",
            title: "NIT Inválido",
            html: `
              <p>El NIT debe tener uno de estos formatos:</p>
              <ul style="text-align: left; margin: 0 auto; display: inline-block;">
                <li><strong>Formato largo:</strong> 0000-000000-000-0 (14 dígitos)</li>
                <li><strong>Formato corto:</strong> 00000000-0 (9 dígitos)</li>
              </ul>
            `,
          });
          return;
        }
      }
      
      if (!codActividad) {
        Swal.fire("Error", "Debe seleccionar una actividad económica", "error");
        return;
      }
    }

    const payload = { 
      nombre, 
      tipo_cliente: tipoCliente,
    };

    if (tipoDocumento && tipoDocumento.trim() !== '') {
      payload.tipo_documento = tipoDocumento;
    }
    
    if (numeroDocumento && numeroDocumento.trim() !== '') {
      payload.numero_documento = numeroDocumento.replace(/-/g, '');
    } else {
      delete payload.tipo_documento;
    }

    if (telefono && telefono.trim() !== '') payload.telefono = telefono;
    if (email && email.trim() !== '') payload.email = email;
    if (direccion && direccion.trim() !== '') payload.direccion = direccion;
    if (departamento && departamento.trim() !== '') payload.departamento = departamento;
    if (municipio && municipio.trim() !== '') payload.municipio = municipio;

    if (tipoCliente === "Contribuyente") {
      payload.nrc = nrc.replace(/-/g, '');
      payload.nombre_comercial = nombreComercial || null;
      payload.cod_actividad = codActividad;
      payload.desc_actividad = descActividad;
    }

    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, payload);
        Swal.fire("Actualizado", "Cliente actualizado correctamente", "success");
      } else {
        await api.post("/clients", payload);
        Swal.fire("Guardado", "Cliente registrado correctamente", "success");
      }

      resetForm();
      setEditingClient(null);
      if (typeof fetchClients === "function") fetchClients();
      if (typeof onCancel === "function") onCancel();
    } catch (error) {
      if (error.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors)[0][0];
        Swal.fire("Error", firstError, "error");
      } else {
        Swal.fire("Error", "Ocurrió un error al guardar el cliente", "error");
        console.error(error);
      }
    }
  };

  return (
    <div className="card" style={{ marginBottom: "24px" }}>
      <div className="card-header">
        <div className="d-flex align-items-center gap-2">
          <UserPlus size={24} color="#667eea" />
          <h2 className="card-title" style={{ fontSize: "20px" }}>
            {editingClient ? "Editar Cliente" : "Nuevo Cliente"}
          </h2>
        </div>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: "16px", marginBottom: "24px" }}>
            <div className="form-group">
              <label className="form-label">Tipo de Cliente *</label>
              <select className="form-control" value={tipoCliente} onChange={(e) => setTipoCliente(e.target.value)}>
                <option value="No Contribuyente">No Contribuyente</option>
                <option value="Contribuyente">Contribuyente</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Nombre Completo *</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ingrese el nombre del cliente"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                ref={nombreRef}
                required
              />
            </div>
          </div>

          <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

          <h4 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px", color: "#2d3748" }}>
            Documento de Identificación {tipoCliente === "Contribuyente" && <span style={{ color: "#e53e3e" }}>*</span>}
          </h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "16px", marginBottom: "24px" }}>
            <div className="form-group">
              <label className="form-label">
                Tipo de Documento {tipoCliente === "Contribuyente" && <span style={{ color: "#e53e3e" }}>*</span>}
              </label>
              <select
                className="form-control"
                value={tipoDocumento}
                onChange={(e) => setTipoDocumento(e.target.value)}
                required={tipoCliente === "Contribuyente"}
              >
                <option value="">Seleccione tipo de documento...</option>
                {tiposDocumento.map((tipo) => (
                  <option 
                    key={tipo.codigo} 
                    value={tipo.codigo}
                    disabled={tipoCliente === "Contribuyente" && tipo.codigo !== "36"}
                  >
                    {tipo.nombre}
                    {tipoCliente === "Contribuyente" && tipo.codigo !== "36" && " (No permitido para contribuyentes)"}
                  </option>
                ))}
              </select>
              {tipoCliente === "Contribuyente" && (
                <small style={{ color: "#ed8936", fontSize: "12px", display: "block", marginTop: "4px" }}>
                  ⚠️ Los contribuyentes solo pueden usar NIT
                </small>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Número de Documento {tipoCliente === "Contribuyente" && <span style={{ color: "#e53e3e" }}>*</span>}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder={
                  tipoDocumento === "13" ? "00000000-0" :
                  tipoDocumento === "36" ? "0000-000000-000-0 o 00000000-0" :
                  "Ingrese el número del documento"
                }
                value={numeroDocumento}
                onChange={(e) => {
                  let valor = e.target.value;
                  
                  if (tipoDocumento === "36") {
                    valor = formatearNIT(valor);
                  } else if (tipoDocumento === "13") {
                    valor = formatearDUI(valor);
                  }
                  
                  setNumeroDocumento(valor);
                  
                  if (!valor && tipoCliente === "No Contribuyente") {
                    setTipoDocumento("");
                  }
                }}
                maxLength={tipoDocumento === "36" ? 17 : tipoDocumento === "13" ? 10 : undefined}
                required={tipoCliente === "Contribuyente"}
              />
              <small style={{ color: "#718096", fontSize: "12px" }}>
                {tipoDocumento === "13" && "Formato DUI: 00000000-0"}
                {tipoDocumento === "36" && "Formato NIT: 0000-000000-000-0 (14 dígitos) o 00000000-0 (9 dígitos)"}
                {tipoDocumento === "03" && "Número de pasaporte"}
                {tipoDocumento === "02" && "Número de carnet de residente"}
                {tipoDocumento === "37" && "Otro tipo de documento"}
              </small>
            </div>
          </div>

          <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

          {tipoCliente === "Contribuyente" && (
            <>
              <h4 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px", color: "#2d3748" }}>
                Información Fiscal
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
                <div className="form-group">
                  <label className="form-label">NRC *</label>
                  <input
                    className="form-control"
                    placeholder="000000-0"
                    value={nrc}
                    onChange={(e) => setNrc(e.target.value)}
                    required
                  />
                  <small style={{ color: "#718096", fontSize: "12px" }}>
                    Puede incluir guión (se guardará sin guión)
                  </small>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Nombre Comercial</label>
                  <input
                    className="form-control"
                    placeholder="Nombre del negocio (opcional)"
                    value={nombreComercial}
                    onChange={(e) => setNombreComercial(e.target.value)}
                  />
                </div>

                <div className="form-group actividad-search-container" style={{ gridColumn: "1 / -1", position: "relative" }}>
                  <label className="form-label">Actividad Económica (CIIU) *</label>
                  {loadingActividades ? (
                    <div style={{ padding: "12px", textAlign: "center" }}>
                      <small style={{ color: "#718096" }}>Cargando actividades...</small>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar actividad económica por código o descripción..."
                        value={actividadSearch}
                        onChange={handleActividadInputChange}
                        onFocus={() => setShowActividadDropdown(true)}
                        required
                      />
                      
                      {showActividadDropdown && (
                        <div style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          maxHeight: "300px",
                          overflowY: "auto",
                          background: "white",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                          zIndex: 1000,
                          marginTop: "4px"
                        }}>
                          {filteredActividades.length === 0 ? (
                            <div style={{ 
                              padding: "16px", 
                              textAlign: "center", 
                              color: "#718096" 
                            }}>
                              No se encontraron actividades
                            </div>
                          ) : (
                            filteredActividades.map((actividad) => (
                              <div
                                key={actividad.codigo}
                                onClick={() => handleActividadSelect(actividad)}
                                style={{
                                  padding: "12px 16px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #f1f5f9",
                                  transition: "background 0.2s",
                                }}
                                onMouseEnter={(e) => e.target.style.background = "#f8fafc"}
                                onMouseLeave={(e) => e.target.style.background = "white"}
                              >
                                <div style={{ fontWeight: 500, color: "#1e293b", marginBottom: "4px" }}>
                                  {actividad.codigo}
                                </div>
                                <div style={{ fontSize: "13px", color: "#64748b" }}>
                                  {actividad.descripcion}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {descActividad && (
                  <div style={{ 
                    gridColumn: "1 / -1", 
                    padding: "12px", 
                    background: "#f0f9ff", 
                    borderRadius: "8px", 
                    border: "1px solid #bae6fd",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <small style={{ color: "#0369a1", fontSize: "13px", fontWeight: 500 }}>
                      📋 Seleccionada: {codActividad} - {descActividad}
                    </small>
                    <button
                      type="button"
                      onClick={() => {
                        setCodActividad('');
                        setDescActividad('');
                        setActividadSearch('');
                      }}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#0369a1",
                        cursor: "pointer",
                        padding: "4px 8px",
                        fontSize: "12px",
                        fontWeight: 500
                      }}
                    >
                      ✕ Limpiar
                    </button>
                  </div>
                )}
              </div>

              <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>
            </>
          )}

          <h4 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px", color: "#2d3748" }}>
            Información de Contacto
          </h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input className="form-control" placeholder="0000-0000" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Correo Electrónico</label>
              <input type="email" className="form-control" placeholder="correo@ejemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "16px", marginBottom: "24px" }}>
            <div className="form-group">
              <label className="form-label">Dirección</label>
              <input className="form-control" placeholder="Dirección completa" value={direccion} onChange={(e) => setDireccion(e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">Departamento</label>
              <select 
                className="form-control" 
                value={departamento} 
                onChange={(e) => setDepartamento(e.target.value)}
              >
                <option value="">Seleccione...</option>
                {departamentos.map((depto) => (
                  <option key={depto.codigo} value={depto.codigo}>{depto.nombre}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Municipio</label>
              <select 
                className="form-control" 
                value={municipio} 
                onChange={(e) => setMunicipio(e.target.value)}
                disabled={!departamento}
              >
                <option value="">
                  {departamento ? "Seleccione..." : "Primero seleccione departamento"}
                </option>
                {municipiosFiltrados.map((muni) => (
                  <option key={`${muni.departamento}-${muni.codigo}`} value={muni.codigo}>
                    {muni.nombre}
                  </option>
                ))}
              </select>
              {departamento && municipiosFiltrados.length === 0 && (
                <small style={{ color: "#e53e3e", fontSize: "12px", display: "block", marginTop: "4px" }}>
                  ⚠️ No hay municipios disponibles para este departamento
                </small>
              )}
            </div>
          </div>

          <div className="d-flex gap-2" style={{ marginTop: "32px" }}>
            <button type="submit" className="btn btn-success">
              <Save size={18} />
              {editingClient ? "Actualizar Cliente" : "Guardar Cliente"}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                resetForm();
                setEditingClient(null);
                if (typeof onCancel === "function") onCancel();
              }}
            >
              <X size={18} />
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}